// ═══════════════════════════════════════════════════════════════════════════
// WRITTN - Cloud Sync Service
// ═══════════════════════════════════════════════════════════════════════════
// Real-time Firestore sync for user data (favorites, journals, settings, analytics)
// Uses debounced writes and last-write-wins conflict resolution
// ═══════════════════════════════════════════════════════════════════════════

import {
    doc,
    collection,
    setDoc,
    deleteDoc,
    onSnapshot,
    serverTimestamp,
    writeBatch,
    getDocs,
    query,
    orderBy
} from 'firebase/firestore';
import { db } from '../config/firebase.js';
import { getCurrentUser, onAuthChange } from './auth.js';

// ─────────────────────────────────────────────────────────────────────────
// STATE
// ─────────────────────────────────────────────────────────────────────────

let isInitialized = false;
let unsubscribeFunctions = [];
let syncEnabled = true;
let pendingWrites = new Map();
let debounceTimers = new Map();

const DEBOUNCE_MS = 500;

// ─────────────────────────────────────────────────────────────────────────
// INITIALIZATION
// ─────────────────────────────────────────────────────────────────────────

export async function initCloudSync() {
    if (isInitialized) return;

    // Listen for auth changes
    onAuthChange(async (user) => {
        if (user) {
            await startSync(user.uid);
        } else {
            stopSync();
        }
    });

    isInitialized = true;
    console.log('Cloud sync initialized');
}

async function startSync(userId) {
    if (!syncEnabled) return;

    console.log('Starting cloud sync for user:', userId);

    // First, pull cloud data to local (initial sync)
    await pullFromCloud(userId);

    // Then set up real-time listeners
    setupListeners(userId);
}

function stopSync() {
    unsubscribeFunctions.forEach(unsub => unsub());
    unsubscribeFunctions = [];
    pendingWrites.clear();
    debounceTimers.forEach(timer => clearTimeout(timer));
    debounceTimers.clear();
    console.log('Cloud sync stopped');
}

// ─────────────────────────────────────────────────────────────────────────
// LISTENERS (Cloud → Local)
// ─────────────────────────────────────────────────────────────────────────

function setupListeners(userId) {
    // Listen to favorites
    const favoritesRef = collection(db, 'users', userId, 'favorites');
    const unsubFavorites = onSnapshot(favoritesRef, (snapshot) => {
        const favorites = [];
        snapshot.forEach(doc => {
            favorites.push({ id: doc.id, ...doc.data() });
        });
        // Store in localStorage without triggering sync back
        setLocalDataSilent('favorites', favorites.map(f => ({
            reference: f.reference,
            text: f.text,
            book: f.book || null,
            chapter: f.chapter || null,
            verseNum: f.verseNum || null,
            category: f.category || null,
            theme: f.theme || null,
            savedAt: f.savedAt?.toDate?.()?.toISOString() || f.savedAt,
            note: f.note || null,
        })));
    });
    unsubscribeFunctions.push(unsubFavorites);

    // Listen to journals
    const journalsRef = collection(db, 'users', userId, 'journals');
    const unsubJournals = onSnapshot(journalsRef, (snapshot) => {
        const journals = [];
        snapshot.forEach(doc => {
            journals.push({ id: doc.id, ...doc.data() });
        });
        setLocalDataSilent('writtn_journal', journals.map(j => ({
            id: j.id,
            verseRef: j.verseRef,
            verseText: j.verseText,
            content: j.content,
            createdAt: j.createdAt?.toDate?.()?.toISOString() || j.createdAt,
            updatedAt: j.updatedAt?.toDate?.()?.toISOString() || j.updatedAt,
        })));
    });
    unsubscribeFunctions.push(unsubJournals);

    // Listen to settings
    const userRef = doc(db, 'users', userId);
    const unsubSettings = onSnapshot(userRef, (docSnap) => {
        if (docSnap.exists()) {
            const data = docSnap.data();
            if (data.settings) {
                const prefs = getLocalData('preferences', {});
                setLocalDataSilent('preferences', {
                    ...prefs,
                    theme: data.settings.theme || prefs.theme,
                });
            }
            if (data.soakTime !== undefined) {
                setLocalDataSilent('soakTime', data.soakTime);
            }
        }
    });
    unsubscribeFunctions.push(unsubSettings);
}

// ─────────────────────────────────────────────────────────────────────────
// SYNC TO CLOUD (Local → Cloud)
// ─────────────────────────────────────────────────────────────────────────

export function syncFavorites(favorites) {
    const user = getCurrentUser();
    if (!user || !syncEnabled) return;

    debouncedWrite('favorites', async () => {
        const batch = writeBatch(db);
        const favoritesRef = collection(db, 'users', user.uid, 'favorites');

        // Get existing favorites to determine what to delete
        const existingSnap = await getDocs(favoritesRef);
        const existingIds = new Set();
        existingSnap.forEach(doc => existingIds.add(doc.id));

        // Set/update all current favorites
        const newIds = new Set();
        favorites.forEach(fav => {
            const id = sanitizeId(fav.reference);
            newIds.add(id);
            const docRef = doc(favoritesRef, id);
            batch.set(docRef, {
                ...fav,
                savedAt: fav.savedAt || new Date().toISOString(),
                syncedAt: serverTimestamp(),
            });
        });

        // Delete removed favorites
        existingIds.forEach(id => {
            if (!newIds.has(id)) {
                const docRef = doc(favoritesRef, id);
                batch.delete(docRef);
            }
        });

        await batch.commit();
        console.log('Favorites synced to cloud');
    });
}

export function syncJournals(journals) {
    const user = getCurrentUser();
    if (!user || !syncEnabled) return;

    debouncedWrite('journals', async () => {
        const batch = writeBatch(db);
        const journalsRef = collection(db, 'users', user.uid, 'journals');

        // Get existing journals
        const existingSnap = await getDocs(journalsRef);
        const existingIds = new Set();
        existingSnap.forEach(doc => existingIds.add(doc.id));

        // Set/update all current journals
        const newIds = new Set();
        journals.forEach(entry => {
            newIds.add(entry.id);
            const docRef = doc(journalsRef, entry.id);
            batch.set(docRef, {
                ...entry,
                syncedAt: serverTimestamp(),
            });
        });

        // Delete removed journals
        existingIds.forEach(id => {
            if (!newIds.has(id)) {
                const docRef = doc(journalsRef, id);
                batch.delete(docRef);
            }
        });

        await batch.commit();
        console.log('Journals synced to cloud');
    });
}

export function syncSettings(settings) {
    const user = getCurrentUser();
    if (!user || !syncEnabled) return;

    debouncedWrite('settings', async () => {
        const userRef = doc(db, 'users', user.uid);
        await setDoc(userRef, {
            settings: {
                theme: settings.theme,
            },
            soakTime: settings.soakTime,
            syncedAt: serverTimestamp(),
        }, { merge: true });
        console.log('Settings synced to cloud');
    });
}

export function syncAnalytics(aggregates) {
    const user = getCurrentUser();
    if (!user || !syncEnabled) return;

    debouncedWrite('analytics', async () => {
        const batch = writeBatch(db);
        const analyticsRef = collection(db, 'users', user.uid, 'analytics');

        aggregates.forEach(agg => {
            const docRef = doc(analyticsRef, agg.date);
            batch.set(docRef, {
                ...agg,
                syncedAt: serverTimestamp(),
            });
        });

        await batch.commit();
        console.log('Analytics synced to cloud');
    });
}

// ─────────────────────────────────────────────────────────────────────────
// INITIAL PULL (Cloud → Local on login)
// ─────────────────────────────────────────────────────────────────────────

async function pullFromCloud(userId) {
    try {
        // Pull favorites
        const favoritesRef = collection(db, 'users', userId, 'favorites');
        const favoritesSnap = await getDocs(query(favoritesRef, orderBy('savedAt', 'desc')));
        if (!favoritesSnap.empty) {
            const favorites = [];
            favoritesSnap.forEach(doc => {
                const data = doc.data();
                favorites.push({
                    reference: data.reference,
                    text: data.text,
                    book: data.book || null,
                    chapter: data.chapter || null,
                    verseNum: data.verseNum || null,
                    category: data.category || null,
                    theme: data.theme || null,
                    savedAt: data.savedAt?.toDate?.()?.toISOString() || data.savedAt,
                    note: data.note || null,
                });
            });
            setLocalDataSilent('favorites', favorites);
            console.log(`Pulled ${favorites.length} favorites from cloud`);
        }

        // Pull journals
        const journalsRef = collection(db, 'users', userId, 'journals');
        const journalsSnap = await getDocs(query(journalsRef, orderBy('createdAt', 'desc')));
        if (!journalsSnap.empty) {
            const journals = [];
            journalsSnap.forEach(doc => {
                const data = doc.data();
                journals.push({
                    id: doc.id,
                    verseRef: data.verseRef,
                    verseText: data.verseText,
                    content: data.content,
                    createdAt: data.createdAt?.toDate?.()?.toISOString() || data.createdAt,
                    updatedAt: data.updatedAt?.toDate?.()?.toISOString() || data.updatedAt,
                });
            });
            setLocalDataSilent('writtn_journal', journals);
            console.log(`Pulled ${journals.length} journal entries from cloud`);
        }

        console.log('Initial cloud pull complete');
    } catch (error) {
        console.error('Error pulling from cloud:', error);
    }
}

// ─────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────

function debouncedWrite(key, writeFn) {
    // Clear existing timer
    if (debounceTimers.has(key)) {
        clearTimeout(debounceTimers.get(key));
    }

    // Set new timer
    const timer = setTimeout(async () => {
        try {
            await writeFn();
        } catch (error) {
            console.error(`Sync error for ${key}:`, error);
        }
        debounceTimers.delete(key);
    }, DEBOUNCE_MS);

    debounceTimers.set(key, timer);
}

function sanitizeId(str) {
    // Firestore document IDs can't contain forward slashes
    return str.replace(/[/\\#$\[\]]/g, '_');
}

function getLocalData(key, defaultValue) {
    try {
        const item = localStorage.getItem('writtn_' + key);
        return item ? JSON.parse(item) : defaultValue;
    } catch {
        return defaultValue;
    }
}

function setLocalDataSilent(key, value) {
    // Set localStorage without triggering sync (to avoid infinite loops)
    try {
        localStorage.setItem('writtn_' + key, JSON.stringify(value));
    } catch (e) {
        console.warn('Storage write error:', e);
    }
}

// ─────────────────────────────────────────────────────────────────────────
// CONTROL FUNCTIONS
// ─────────────────────────────────────────────────────────────────────────

export function pauseSync() {
    syncEnabled = false;
}

export function resumeSync() {
    syncEnabled = true;
}

export function isSyncEnabled() {
    return syncEnabled && getCurrentUser() !== null;
}

export default {
    initCloudSync,
    syncFavorites,
    syncJournals,
    syncSettings,
    syncAnalytics,
    pauseSync,
    resumeSync,
    isSyncEnabled,
};
